import { AlternateColorProps } from '@components/Product/Details/_ProductSelection';
import { MetaProps } from '@repo/ui/src/types/meta';
import { SeoProps } from '@repo/ui/src/types/seo';
import { AssetProps, ImageProps, MediaType } from '@repo/ui/src/types/image';
import { LinkProps } from '@repo/ui/src/types/link';
import { CurrentUrlProps } from '@repo/ui/src/types/currentUrl';
import Product from '@models/product';
import { Recommendations, GiftCard } from '@repo/ui/src/types/basket';
import { OrderLineItemData } from '@repo/ui/src/types/lineItem';
import { ProductOption } from '@repo/ui/src/types/product';

export interface FrontFacingImagesProps {
  closedImage: ImageProps;
  openImage: ImageProps;
}

export interface InventoryProps {
  _type: string;
  ats: number;
  orderable: boolean;
  preorderable: boolean;
  stock_level: number;
  in_stock_date: string;
}

export interface ProductRowProps {
  title: string;
  link?: LinkProps;
  description: string;
  results: {
    hits: Array<ProductListProps>;
  };
  category?: {
    categories: string;
  };
}

export interface ExtendedWarrantyFormProps {
  grillSerialNumberForm: {
    label: string;
    errorText: string;
  };
  findSerialNumberDialog: {
    linkText: string;
    title: string;
    text: string;
    image: MediaType;
    currentUrl: CurrentUrlProps;
  };
  dateOfPurchaseForm: {
    label: string;
    errorText: string;
  };
  submitButtonText: string;
  manufacturerWarrantyLength: number;
}

export interface CapacityProps {
  capacity: string;
  totalCookingSpace: string;
  pelletHopperCapacity: string;
  totalWeight: string;
  maxTemperature: string;
  compareWithOtherGrills: string;
  measurements: string;
  interior: string;
  exterior: string;
}

export interface GrillCapacityProps {
  image: MediaType;
  foodTitle: string;
  amount: string;
}

export interface ProductListProps {
  _meta: MetaProps;
  listName?: string;
  index: number;
}

export interface AddonProps {
  sku: string;
  label: string;
  overlayImage: AssetProps;
}

export interface Promotions {
  icon: string;
  id: string;
  title: string;
}

export interface ProductDataProps {
  categories: Array<{
    _meta: MetaProps;
    categories: string;
  }>;
  dimensions: {
    unitOfMeasure: string;
    height: number;
    width: number;
    depth: number;
    weight: number;
    hopperCapacity: number;
  };
  cookingSpace: {
    tier: Array<{
      width: number;
      depth: number;
    }>;
    unitOfMeasure: string;
  };
  maxTemp: {
    unitOfMeasure: string;
    temperature: number;
  };
  meatProbes: {
    qty: number;
  };
  sku: string;
  price: string;
  pairings: string[];
  storefrontType: string;
  newArrivalStart: string;
  newArrivalEnd: string;
  grillSeries: string;
  upc: string;
}

export interface ProductModelProps {
  _meta?: MetaProps;
  productData?: ProductDataProps;
  title?: string;
  shortTitle?: string;
  shortDescription?: string;
  boldDescription?: string;
  description?: string;
  shop?: ShopDataProps;
  shopData?: ShopDataProps;
  images?: Array<AssetProps>;
  frontFacingImages?: FrontFacingImagesProps;
  transparentImages?: MediaType[];
  color?: string;
  quantity?: number;
  mainImageUrl?: string;
  monetate?: Recommendations;
  alternateSizes?: {
    sizes: Array<LinkProps>;
  };
  lineItem?: OrderLineItemData;
  addOn?: AddonProps;
  smallHeading?: string;
  currency?: string;
  giftCard?: GiftCard;
  compatibleWith?: Array<{
    link: LinkProps;
    image: {
      altTag: string;
      static: string;
      image: {
        _meta: MetaProps;
        accessibilityLabel: string;
        defaultHost: string;
        id: string;
        name: string;
        endpoint: string;
        url: any;
      };
    };
  }>;
  ratingSummary?: {
    id: string;
    average: number;
    count: number;
    distribution: Array<{
      Count: number;
      RatingValue: number;
    }>;
  };
  options?: Array<ProductOption>;
  parentProduct?: Product;
}

export interface ProductDetailProps {
  seo: SeoProps[];
  title: string;
  description: string;
  productData: ProductDataProps;
  images: Array<AssetProps>;
  color: string;
  productAlertMessage: string;
  showBreadcrumb: boolean;
  breadcrumb: Array<LinkProps>;
  contentRows: any;
  alternateSizes?: {
    sizes: Array<LinkProps>;
  };
  shopData: ShopDataProps;
  alternateColorsV2?: AlternateColorProps;
  prop65?: {
    title: string;
    warningBody: string;
  };
  ownerManual: {
    ownerManualLink: Array<LinkProps>;
  };
  extendedWarranty?: ExtendedWarrantyFormProps;
  sizes: Array<{
    shortTitle: string;
    shopData: ShopDataProps;
    frontFacingImages: FrontFacingImagesProps;
  }>;
  addOns: {
    label: string;
    addons: {
      overlay_image?: AssetProps;
      sku: string;
    };
  };
  transparentImages: AssetProps[];
  lifestyleImages: AssetProps[];
  lifestyleVideos: AssetProps[];
  spinSetUrl?: string;
  template: 'PDP V2' | 'ZR Grill PDP' | 'ZR Non-Grill Dark' | 'PDP V2 Light' | any;
  frontFacingImages: FrontFacingImagesProps;
  smallHeading: string;
  links: any;
}

export enum VariationGroup {
  grillColor = 'grill_color',
  grillSize = 'grill_size',
  category = 'product_group',
  apparelSize = 'apparel_size',
  apparelColor = 'apparel_color',
}
export interface VariationValue {
  value: string;
}
export interface ActiveVariationValue extends VariationValue {
  name: VariationGroup;
}

export interface VariationAttribute {
  id: VariationGroup;
  name: string;
  values: Array<VariationValue>;
}

export interface ShopDataProps {
  description: string;
  id: string;
  originalPrice: number;
  color: string;
  currency: string;
  inventory: InventoryProps;
  step_quantity: number;
  price: number;
  productUrl: string;
  ctaButtonType?: any;
  mainCategory: string;
  mainImageUrl: string;
  discount: number;
  disableLocally: boolean;
  isGrill: boolean;
  isExtendedWarranty: boolean;
  warrantyPlanId?: string;
  promotionIds: string;
  weight: string;
  title: string;
  upc?: string;
  customFields: string;
  warrantySkus: Array<string>;
  promotions?: Array<Promotions>;
  variationAttributes: Array<VariationAttribute>;
  variations: Array<ProductVariation>;
  variationValues: Array<ActiveVariationValue>;
}

export interface ProductVariation
  extends Pick<
    ShopDataProps,
    | 'id'
    | 'title'
    | 'productUrl'
    | 'mainCategory'
    | 'mainImageUrl'
    | 'originalPrice'
    | 'price'
    | 'discount'
    | 'variationValues'
    | 'inventory'
    | 'ctaButtonType'
  > {
  customFields: string;
  grillSize?: string;
  colorVariationImage: string;
  sku: string;
  description: string;
  attributesMap: Record<VariationGroup, string>;
  isOrderable: boolean;
}
